import { defineStore } from "pinia"
import { computed, watch, nextTick } from "vue"
import Cookies from 'js-cookie'
import { detail } from '@/api/device/station'
import { getStationBaseData } from '@/api/station'
import { nameList } from '@/api/device/station'
import { listModel } from '@/api/device/model'
import { DEVICE_FLAG_BITS, hasDeviceFlag } from '@/utils/deviceFlag'

/**
 * 电站列表头部信息类型定义
 * @typedef {Object} HeaderStation
 * @property {number|string} stationId - 电站ID
 * @property {string} name - 电站名称
 * @property {string} equipmentModel - 设备型号
 * @property {number} energyStorageType - 储能类型
 * @property {number} pvAccess - 光伏接入
 * @property {number} chargingPileAccess - 充电桩接入
 * @property {number} stationState - 储能类型
 */

const useAppStore = defineStore(
  'app',
  {
    state: () => ({
      sidebar: {
        opened: Cookies.get('sidebarStatus') ? !!+Cookies.get('sidebarStatus') : true,
        withoutAnimation: false,
        hide: false
      },
      device: 'desktop',
      size: 'default',

      /**
       * @type HeaderStation[]
       */
      stations: [],
      stationId: null,
      modelList: [],
      stationBaseData: {},
      stationBaseDataLoading: false,
      // stationBaseData 实际对应的电站。stationBaseData 被 persist 到 localStorage，
      // 切站瞬间里面还是上一个电站的快照，据此判断能力位会用错电站
      stationBaseDataOwnerId: null,
      // getStations 的在途请求句柄,用于并发去重。非持久化(见下方 persist 配置)
      _stationsPromise: null,
      // getBaseData 的在途请求句柄与其对应的电站，用于并发去重。非持久化
      _baseDataPromise: null,
      _baseDataPromiseId: null,
    }),
    getters: {
      currentStation: (state) => {
        if (!state.stations || !Array.isArray(state.stations)) {
          return undefined;
        }
        return state.stations.find(item => state.stationId == item.stationId)
      },

      has100c: (state) => {
        if (!state.currentStation?.equipmentModel) {
          return false;
        }
        return state.currentStation.equipmentModel.includes('eBlock100C')
      },
      hasEb100C: (state) => {
        if (!state.currentStation?.equipmentModel) {
          return false;
        }
        return state.currentStation.equipmentModel.includes('eBlock100C')
      },
      eBlockModels() {
        return this.filterModels('eBlock')
      },
      eLinkModels() {
        return this.filterModels('eLink')
      },
      incomeModel() {
        if (!this.stations || !Array.isArray(this.stations)) {
          return undefined;
        }
        const station = this.stations.find(
          item => this.stationId == item.stationId
        );
        return station?.incomeModel;
      },
      stationName() {
        if (!this.stations || !Array.isArray(this.stations)) {
          return undefined;
        }
        const station = this.stations.find(
          item => this.stationId == item.stationId
        );
        return station?.stationName || station?.name;
      },
      isOldStation() {
        if (!this.stations || !Array.isArray(this.stations)) {
          return undefined;
        }
        const station = this.stations.find(
          item => this.stationId == item.stationId
        );
        return station?.isOldStation;
      },
      addr() {
        if (!this.stations || !Array.isArray(this.stations)) {
          return undefined;
        }
        const station = this.stations.find(
          item => this.stationId == item.stationId
        );
        return station?.addr;
      },
      // 允许虚拟电厂调用
      strategySwitch: (state) => {
        const station = state.stations.find(
          item => state.stationId == item.stationId
        );
        return station?.strategySwitch;
      },
      /**
       * 现货收益结算是否开启（deviceFlag bit5）。
       * 三态：true / false / undefined。undefined 表示 stationBaseData 尚未就绪，
       * 或里面还是上一个电站的持久化快照——此时 UI 不应渲染切换器，避免先按抄表
       * 渲染再跳成现货的闪烁。
       */
      spotSettlementEnabled: (state) => {
        if (state.stationId == null) return undefined
        // 快照归属校验：切站瞬间 stationBaseData 可能是上一个电站的
        if (state.stationBaseDataOwnerId == null) return undefined
        if (String(state.stationBaseDataOwnerId) !== String(state.stationId)) return undefined
        const flag = state.stationBaseData?.deviceFlag
        if (flag == null) return undefined
        return hasDeviceFlag(flag, DEVICE_FLAG_BITS.INTERVAL_ENERGY)
      }
    },
    actions: {
      async getBaseData(stationId) {
        if (!stationId) return
        // 在途去重：StationStatusBar 与各站级 Tab 会并发调用，共享同一个请求
        if (this._baseDataPromise && String(this._baseDataPromiseId) === String(stationId)) {
          return this._baseDataPromise
        }
        this._baseDataPromiseId = stationId
        this._baseDataPromise = this._doGetBaseData(stationId).finally(() => {
          // 释放句柄，允许下次调用重新发起（否则一次失败会永久锁死）
          if (String(this._baseDataPromiseId) === String(stationId)) {
            this._baseDataPromise = null
            this._baseDataPromiseId = null
          }
        })
        return this._baseDataPromise
      },

      async _doGetBaseData(stationId) {
        this.stationBaseDataLoading = true
        // 取数期间先作废归属标记，避免这段时间里读到上一个电站的能力位
        this.stationBaseDataOwnerId = null
        try {
          const [res, extRes] = await Promise.all([
            getStationBaseData(stationId),
            detail(stationId)
          ])
          if (res.code == 200) {
            this.stationBaseData = res.data || {}
          }
          Object.assign(this.stationBaseData, extRes?.data?.station || {})
          this.stationBaseDataOwnerId = stationId

        } catch (e) {
          console.error('Failed to get station base data:', e)
        } finally {
          this.stationBaseDataLoading = false
        }
      },

      getStationById(stationId) {
        if (!this.stations || !Array.isArray(this.stations)) {
          return undefined;
        }
        let station = this.stations.find(item => stationId == item.stationId)
        return station
      },
      getMeterModel(stationId) {
        let station = this.getStationById(stationId)
        return station?.meterModel
      },
      toggleSideBar(withoutAnimation) {
        if (this.sidebar.hide) {
          return false;
        }
        this.sidebar.opened = !this.sidebar.opened
        this.sidebar.withoutAnimation = withoutAnimation
        if (this.sidebar.opened) {
          Cookies.set('sidebarStatus', 1)
        } else {
          Cookies.set('sidebarStatus', 0)
        }
      },
      closeSideBar(withoutAnimation) {
        Cookies.set('sidebarStatus', 0)
        this.sidebar.opened = false
        this.sidebar.withoutAnimation = withoutAnimation
      },
      toggleDevice(device) {
        this.device = device
      },
      setSize(size) {
        this.size = size;
        Cookies.set('size', size)
      },
      toggleSideBarHide(status) {
        this.sidebar.hide = status
      },
      /**
       * 拉取电站名称列表。
       * 调用方较多(layout、登录、OTA 等页面的兜底守卫),这里用 _stationsPromise 做并发去重:
       * 同一时刻的重复调用复用同一个请求,避免登录后 layout 与页面守卫各打一次。
       * 返回 Promise,便于调用方在需要时 await。
       */
      getStations() {
        if (this._stationsPromise) return this._stationsPromise

        this._stationsPromise = nameList().then(res => {
          this.stations = res?.data || []
          const list = Array.isArray(res?.data) ? res.data : []
          // 当前 stationId 为空,或已不在新拉取的电站列表中(如超时重登录后残留的旧电站),
          // 回退到列表第一个,避免 currentStation/stationName 定位不到电站而报错
          const isValid = this.stationId != null &&
            list.some(item => this.stationId == item.stationId)
          if (!isValid) {
            this.setStationId(list.length > 0 ? list[0].stationId : null)
          }
        }).catch(err => {
          console.error('Failed to get stations list:', err)
          this.stations = []
        }).finally(() => {
          // 释放句柄,失败后允许下次调用重新发起(否则一次失败会永久锁死)
          this._stationsPromise = null
        })

        return this._stationsPromise
      },

      setStationId(id) {
        this.stationId = id
        // local_storage.save('stationId', id)
      },

      /**
       * 重置电站相关状态(登出时调用)。
       * app store 开启了 persist,stationId/stations 会持久化到 localStorage,
       * 若不清理,超时重登录后会残留上一个账号的电站,导致定位不到电站。
       */
      resetStationState() {
        this.stations = []
        this.stationId = null
        this.stationBaseData = {}
        this.stationBaseDataOwnerId = null
        this.stationBaseDataLoading = false
      },

      async getModelList() {
        try {
          let res = await listModel({
            pageNum: 1,
            pageSize: 65535,
          })
          this.modelList = res?.rows || []
        } catch (err) {
          console.error('Failed to get model list:', err)
          this.modelList = []
        }
      },

      filterModels(modelType) {
        if (!this.modelList || !Array.isArray(this.modelList)) {
          return [];
        }
        return this.modelList.filter(item => {
          return item.modelType == modelType
        })
      },

      /**
       * 获取stationId并监听其变化
       * @param {Object} options - 配置选项
       * @param {Function} options.stationIdWatch - stationId变化时的回调函数
       * @returns {Object} 返回包含stationId的对象
       */
      useStationState(options = {}) {
        const { stationIdWatch } = options;

        // 使用 computed 创建响应式的 stationId
        const stationId = computed(() => this.stationId);

        const result = {
          stationId
        };

        if (stationIdWatch && typeof stationIdWatch === 'function') {
          // 延迟设置 watch，确保 stationId 已经被解构
          nextTick(() => {
            // 监听变化
            watch(stationId, (newVal, oldVal) => {
              if (newVal !== oldVal) {
                // 延迟执行回调，确保 stationId 已经被解构
                nextTick(() => {
                  stationIdWatch();
                });
              }
            });

            // 手动触发一次初始回调（不使用 immediate，避免闭包问题）
            nextTick(() => {
              stationIdWatch();
            });
          });
        }

        return result;
      },
    },
    // _stationsPromise 是运行时的请求句柄,不能序列化,持久化时需排除:
    // 否则刷新后会从 localStorage 还原出一个非 Promise 的值,导致 getStations 直接 return 该值而不再发请求
    // (pinia-plugin-persistedstate v4 用 pick,v3 的 paths 已废弃)
    persist: {
      pick: ['sidebar', 'device', 'size', 'stations', 'stationId', 'modelList', 'stationBaseData', 'stationBaseDataOwnerId'],
    },
  })

export default useAppStore
export { useAppStore }
